import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/components/AuthContext';
import { type GenAudioKind } from './genAudio';
import { getGenAudioUrl } from './audioCache';

/**
 * Shared, race-safe controller for GEN_AUDIOS "one-shot" voice notes (screen-entry
 * notes, etc.) — the same shared-element + monotonic-token pattern proven for the
 * camera category-tab notes, hoisted to a MODULE singleton so notes from different
 * screens can never overlap: one <audio> element and one generation token guard
 * every GEN_AUDIOS one-shot. The latest play always wins; a still-resolving or
 * mid-playing earlier note is stopped instantly; nothing bleeds across screens.
 *
 * (The camera screen keeps its own local instance intentionally — it's a separate
 * route that never coexists with these screens — so this change stays additive.)
 */

let sharedEl: HTMLAudioElement | null = null;
let playToken = 0;

function ensureEl(): HTMLAudioElement {
  if (!sharedEl) sharedEl = new Audio();
  return sharedEl;
}

/** Hard-stop the shared element: pause, rewind, drop src so buffering can't continue. */
function hardStop(el: HTMLAudioElement): void {
  el.pause();
  try { el.currentTime = 0; } catch { /* not seekable yet */ }
  el.removeAttribute('src');
  el.load();
}

/** Stop any GEN_AUDIOS one-shot and invalidate any in-flight resolve. */
export function stopGenAudio(): void {
  playToken++;
  if (sharedEl) hardStop(sharedEl);
}

/**
 * FIX-CAPTURE-TRANCHE-2B-W3 — play an ALREADY-RESOLVED audio URL on the SAME shared element
 * and the SAME monotonic token as the GEN_AUDIOS one-shots, so a capture question and a
 * screen-entry note can never overlap. This is an extension of the ONE controller, not a
 * second one: no new <audio> element, no second token.
 *
 * Returns false when the play is superseded or rejected; `onError` additionally fires if the
 * element raises an `error` event later (an expired or 404 signed URL surfaces that way).
 * A caller that must not show a silent card uses both signals.
 */
export async function playAudioUrl(url: string, onError?: () => void): Promise<boolean> {
  const myToken = ++playToken;
  const el = ensureEl();
  hardStop(el);
  if (myToken !== playToken) return false;

  const fail = () => { if (myToken === playToken) onError?.(); };
  el.addEventListener('error', fail, { once: true });

  el.src = url;
  try { el.currentTime = 0; } catch { /* not seekable yet */ }
  try {
    await el.play?.();
    return myToken === playToken;
  } catch {
    el.removeEventListener('error', fail);
    onError?.();
    return false;
  }
}

/**
 * Play a GEN_AUDIOS one-shot for the farmer's country. Accepts one kind or an
 * ordered list of candidate kinds (first that resolves wins) — used to prefer a
 * convention-correct filename and fall back to a live-uploaded variant.
 *
 * Race-safe: claim a token, stop whatever is playing immediately, resolve the
 * signed URL, then play ONLY if this call is still the latest (a newer play or a
 * stopGenAudio() during the async resolve aborts this one). Fully additive —
 * missing file / null URL / blocked playback fail silent.
 */
export async function playGenAudioClip(
  kinds: GenAudioKind | GenAudioKind[],
  countryCode: string | null | undefined,
): Promise<void> {
  const myToken = ++playToken;
  const el = ensureEl();
  hardStop(el);

  // Cache-first playable URL (local blob on hit → no Supabase call; signed-URL
  // fetch + cache on miss). Candidate list resolved inside getGenAudioUrl.
  const url = await getGenAudioUrl(kinds, countryCode);
  // A newer play (or a stop) happened while we were resolving → abort.
  if (myToken !== playToken) return;
  if (!url) return; // missing / null → silent

  el.src = url;
  try { el.currentTime = 0; } catch { /* noop */ }
  el.play().catch(() => { /* autoplay/network blocked → silent */ });
}

/**
 * Play a screen's GEN_AUDIOS entry note on EVERY navigation to `routePath` — not
 * once-per-mount and not once-per-session. Keyed on the router pathname so it
 * fires on the enter transition (including returning to the screen), and stops the
 * note when the farmer leaves (cleanup) so it never bleeds into the next screen.
 * The module token also debounces a single navigation double-firing into one play.
 */
export function useScreenEntryAudio(
  kinds: GenAudioKind | GenAudioKind[],
  routePath: string,
): void {
  const { user } = useAuth();
  const location = useLocation();
  const countryCode = user?.countryCode;
  // Stable primitive dep so an inline array literal doesn't re-run every render.
  const kindKey = Array.isArray(kinds) ? kinds.join('|') : kinds;

  useEffect(() => {
    if (location.pathname !== routePath) return;
    const list = kindKey.split('|') as GenAudioKind[];
    void playGenAudioClip(list, countryCode);
    return () => { stopGenAudio(); };
  }, [location.pathname, routePath, kindKey, countryCode]);
}
